import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineRounded";
import type { Theme } from "@mui/material";
import { Button, Typography } from "@mui/material";
import { deepmerge } from "@mui/utils";
import type { GridColDef } from "@mui/x-data-grid-premium";
import { useMemo, useState } from "react";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import {
  DataGridColumnHeadersWithToolbar,
  DataGridHeaderToolbarProvider,
} from "@entrilia/shared/components/grid/DataGridColumnHeadersWithToolbar";
import { getRowSelectionModelFromIds, getSelectedRowIdsFromModel } from "@entrilia/shared/components/grid/gridHelper";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import { convertISODate } from "@entrilia/shared/utilities/dateUtils";
import type { OrganizationMember } from "../../../../../api/types/userManagementTypes";
import { getCheckedGridSx } from "../../../../common/grid/gridStyles";
import { formatClientUserType, getDefaultGridSx } from "../definitions";
import ManagedMemberDetails from "../invite-members/ManagedMemberDetails";
import MembersContext from "../MembersContext";
import MemberRowActionsMenu from "./MemberRowActionsMenu";
import { CompanyCell, MemberRoleCell } from "./membersGridDefinitions";

const getMembersGridColumns = ({ currentUserId }: { currentUserId: string }): GridColDef<OrganizationMember>[] => [
  {
    field: "name",
    headerName: "Name",
    flex: 1.5,
    renderCell: ({ row }) => (
      <ManagedMemberDetails userName={row.name} email={row.email} isCurrentUser={row.userId === currentUserId} />
    ),
  },
  {
    field: "userType",
    headerName: "Type",
    flex: 1,
    valueFormatter: (value) => (value ? formatClientUserType(value) : ""),
  },
  {
    field: "organizationPermissions",
    headerName: "Role",
    flex: 1,
    renderCell: ({ row }) => <MemberRoleCell roles={row.organizationPermissions.roles} />,
  },
  {
    field: "clientsAccess",
    headerName: "Companies",
    flex: 1,
    renderCell: ({ row }) => <CompanyCell member={row} />,
  },
  {
    field: "firstAppearanceDate",
    headerName: "First Invited",
    width: 120,
    valueFormatter: (value) => (value ? convertISODate(value) : ""),
  },
  {
    field: "lastActivity",
    headerName: "Last Activity",
    width: 120,
    valueFormatter: (value) => (value ? convertISODate(value) : ""),
  },
];

const getRowId = (row: OrganizationMember) => row.userId;

const MembersGrid = () => {
  const {
    members,
    filteredMembers,
    isLoading,
    selectedMemberIds,
    setSelectedMembersIds,
    hasAccessToManageOrganizationUsers,
    deactivateMembers,
    currentUserData,
  } = useDefinedContext(MembersContext);
  const [menuDisplayedMember, setMenuDisplayedMember] = useState<OrganizationMember | undefined>(undefined);
  const [currentUserId] = currentUserData;

  const columns = useMemo(() => {
    const cols: GridColDef<OrganizationMember>[] = getMembersGridColumns({ currentUserId });
    if (hasAccessToManageOrganizationUsers) {
      cols.push({
        field: "actions",
        headerName: "Actions",
        width: 80,
        cellClassName: "grid-row-actions",
        align: "right",
        headerAlign: "right",
        renderCell: ({ row }) => <MemberRowActionsMenu onSelected={setMenuDisplayedMember} member={row} />,
      });
    }
    return cols;
  }, [currentUserId, hasAccessToManageOrganizationUsers]);

  const showToolbar = hasAccessToManageOrganizationUsers && !!selectedMemberIds?.length;
  const isCurrentUserSelected = selectedMemberIds?.some((id) => id === currentUserId);
  const disableDeactivateSelected = selectedMemberIds?.length === members.length || isCurrentUserSelected;

  const getGridSx = (theme: Theme) => deepmerge(getDefaultGridSx(theme), getCheckedGridSx(theme), { clone: true });

  return (
    <DataGridHeaderToolbarProvider
      showToolbar={showToolbar}
      selection={[selectedMemberIds ?? [], setSelectedMembersIds]}
      actions={
        <>
          <Button
            disabled={disableDeactivateSelected}
            onClick={() => deactivateMembers(selectedMemberIds ?? [])}
            color="error"
            variant="text"
            startIcon={<DeleteOutlineIcon />}
          >
            Deactivate
          </Button>
          {isCurrentUserSelected && (
            <Typography sx={{ ml: 1 }} variant="caption" color="textSecondary">
              (Current user is selected but is not allowed to be deactivated)
            </Typography>
          )}
        </>
      }
    >
      <DataGrid<OrganizationMember>
        checkboxSelection
        rowSelectionModel={getRowSelectionModelFromIds(selectedMemberIds ?? [])}
        onRowSelectionModelChange={(newSelection) => {
          setSelectedMembersIds(getSelectedRowIdsFromModel(newSelection));
        }}
        getRowId={getRowId}
        sx={(theme) => getGridSx(theme)}
        columns={columns}
        rows={filteredMembers}
        multilineRows
        getRowClassName={({ row }) => {
          if (row.userId === menuDisplayedMember?.userId) {
            return "Mui-hovered";
          }
          if (isCurrentUserSelected && row.userId === currentUserId) {
            return "current-member-row";
          }
          if (row.userType === "Support") {
            return "support-member-row";
          }
          return "";
        }}
        loading={isLoading}
        noRowsText="No members"
        disableColumnSorting
        slots={{ columnHeaders: DataGridColumnHeadersWithToolbar }}
      />
    </DataGridHeaderToolbarProvider>
  );
};

export default MembersGrid;
